/**
 * Unified message inbox at `/inbox/threads`.
 *
 * Lists every Provider ↔ Client conversation the current user is part
 * of — automatically combining provider-side threads (when the user
 * has an approved provider profile) and client-side threads (every
 * user). Each row is a deep-link to `ThreadView.jsx` and shows the
 * last-message preview, who sent it, the unread badge, and the time.
 *
 * Polls /api/provider-threads every 30s while open so new messages
 * surface without forcing a refresh.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { ChatCircleDotsIcon as ChatCircleDots, GraduationCapIcon as GraduationCap, ArrowRightIcon as ArrowRight, BriefcaseIcon as Briefcase, UserIcon as User } from "@phosphor-icons/react";

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const sec = Math.max(0, (Date.now() - t) / 1000);
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

export default function MessagesPage() {
  const [threads, setThreads] = useState(null);
  const [isProvider, setIsProvider] = useState(false);
  const navigate = useNavigate();

  const reload = () => {
    api.get("/provider-threads")
      .then((r) => {
        setThreads(r.data?.threads || []);
        setIsProvider(!!r.data?.is_provider);
      })
      .catch(() => setThreads([]));
  };

  useEffect(() => {
    reload();
    const id = setInterval(reload, 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10 py-8 font-manrope text-ink">
        <div className="flex flex-col gap-2 mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold">Inbox</p>
          <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight">Messages</h1>
          <p className="text-sm text-[#666] max-w-2xl">
            {isProvider
              ? "Every conversation between you and your clients lives here. Tap a thread to reply, share photos, and keep delivery details on-platform — never on personal text."
              : "Every conversation between you and the service pros you've booked. Tap a thread to ask questions, share instructions, and get post-service photo updates."}
          </p>
        </div>

        {threads === null ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : threads.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-10 text-center" data-testid="messages-empty">
            <ChatCircleDots size={36} className="mx-auto text-[#9b9b9b]" />
            <p className="mt-3 font-spectral text-xl">No conversations yet.</p>
            <p className="mt-1 text-sm text-[#666] max-w-md mx-auto">
              {isProvider
                ? "When a client books you or messages from your profile, the thread will show up right here."
                : "Tap “Message” on any provider's profile to start a conversation. Bookings auto-open a thread too."}
            </p>
            <Link
              to="/find-pros"
              className="inline-flex items-center gap-1.5 mt-5 rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white px-5 py-2 text-sm font-semibold tracking-wide transition-colors"
              data-testid="messages-find-pros"
            >
              <Briefcase size={14} /> Browse service pros
            </Link>
          </div>
        ) : (
          <div className="space-y-2" data-testid="messages-list">
            {threads.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => navigate(`/inbox/threads/${t.id}`)}
                data-testid={`messages-row-${t.id}`}
                className="w-full text-left bg-white hover:bg-[#FBFBFB] border border-[#E5E5E5] hover:border-[#D5D5D5] rounded-xl p-4 transition-colors flex items-center gap-3"
              >
                <div className="h-11 w-11 rounded-full bg-gradient-to-br from-[#0E0E0E] to-[#1f1f1f] grid place-items-center text-gold shrink-0">
                  {t.provider_user_id ? <GraduationCap size={18} /> : <User size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-spectral text-base truncate">
                      {isProvider ? (t.client_name || "Client") : (t.provider_name || "Provider")}
                    </h3>
                    <span className="text-xs text-[#888] shrink-0">{relTime(t.last_message_at)}</span>
                  </div>
                  <p className="text-sm text-[#666] truncate">
                    {t.last_message_from === "system" && (
                      <span className="text-[#999] mr-1">System:</span>
                    )}
                    {t.last_message_preview || "(no messages yet — say hi!)"}
                  </p>
                </div>
                {((isProvider && t.unread_for_provider > 0) || (!isProvider && t.unread_for_client > 0)) && (
                  <span className="bg-[#C1272D] text-white text-xs font-bold rounded-full min-w-[24px] h-6 px-2 flex items-center justify-center shrink-0">
                    {isProvider ? t.unread_for_provider : t.unread_for_client}
                  </span>
                )}
                <ArrowRight size={16} className="text-[#bbb] shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
